import { useEnvironmentVariables } from "#app/EnvironmentProvider.tsx";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useGetCurrentUserSuspenseQuery } from "#lib/useGetCurrentUserSuspenseQuery.ts";
import { Flex, Heading, Link, LinkButton, Stack, Text } from "@hopper-ui/components";
import { RefreshIcon, SharedIcon } from "@hopper-ui/icons";
import { Div } from "@hopper-ui/styled-system";
import "./QuotaUsagePopover.css";

interface Props {
    currentUsage: number;
    progress: number;
    limit: number;
    remaining: number;
}

export const QuotaUsagePopover = ({ currentUsage, progress, limit, remaining }: Props) => {
    const { tShared } = useSharedTranslation();
    const { managementAppBaseUrl } = useEnvironmentVariables();
    const { data: { isAdmin } } = useGetCurrentUserSuspenseQuery();

    return (
        <Stack gap="stack-md">
            <Flex alignItems="center" gap="inline-sm">
                <Stack gap="stack-xs">
                    <Heading size="sm">{tShared("quotaUsagePopover.title")}</Heading>
                    <Text size="sm" color="neutral-weak">{tShared("quotaUsagePopover.subtitle")}</Text>
                </Stack>
            </Flex>
            <Flex
                direction="column"
                backgroundColor="neutral-weakest"
                padding="inset-md"
                gap="inline-sm"
                borderRadius="rounded-md"
            >
                <Flex justifyContent="space-between" alignItems="center">
                    <Flex gap="stack-xs" alignItems="baseline">
                        <Text
                            size="lg"
                            fontWeight="body-lg-semibold"
                            color="neutral"
                        >
                            {currentUsage}
                        </Text>
                        <Text size="sm" color="neutral-weak">{tShared("quotaUsagePopover.creditsUsed", { limit })}</Text>
                    </Flex>
                    <Flex gap="stack-xs" alignItems="baseline">
                        <Text
                            size="lg"
                            fontWeight="body-lg-semibold"
                            color="neutral"
                        >
                            {remaining}
                        </Text>
                        <Text size="sm" color="neutral-weak">{tShared("quotaUsagePopover.creditsLeft", { count: remaining })}</Text>
                    </Flex>
                </Flex>
                <Div
                    width="100%"
                    backgroundColor="neutral-weak"
                    height="core_80"
                    borderRadius="circle"
                >
                    <Div
                        width={`${progress}%`}
                        height="100%"
                        borderRadius="circle"
                        className="wai-quota-usage-progress-bar-fill"
                    />
                </Div>
                <Flex alignItems="center" gap="inline-xs">
                    <RefreshIcon size="sm" color="neutral-weak" />
                    <Text size="sm" color="neutral-weak">
                        {tShared("quotaUsagePopover.resetsMessage")}
                    </Text>
                </Flex>
            </Flex>
            <Flex
                alignItems="flex-start"
                gap="inline-sm"
                backgroundColor="neutral-weakest"
                padding="inset-md"
                borderRadius="rounded-md"
            >
                <SharedIcon
                    size="sm"
                    color="neutral-weak"
                    marginTop="core_40"
                />
                <Text size="sm" color="neutral-weak">
                    {tShared("quotaUsagePopover.sharedCreditsMessage")}
                </Text>
            </Flex>
            {isAdmin ? (
                <LinkButton
                    size="md"
                    isFluid
                    href={`${managementAppBaseUrl}/settings/billing`}
                    className="wai-quota-usage-upgrade-button"
                    border="transparent"
                    transition="transform 0.2s ease"
                >
                    {tShared("quotaUsagePopover.goUnlimited")}
                </LinkButton>
            ) : (
                <Text
                    size="sm"
                    color="neutral-weak"
                    textAlign="center"
                    style={{ "textWrap": "balance" }}
                >
                    {tShared("quotaUsagePopover.contactAdminForUnlimitedCredits")}
                </Text>
            )}
            <Flex justifyContent="center">
                <Link
                    href="https://help.workleap.com/en/articles/13355804-workleap-ai-usage-and-credits"
                    variant="secondary"
                    isExternal
                    isQuiet
                >
                    {tShared("quotaUsagePopover.learnMoreAboutCredits")}
                </Link>
            </Flex>
        </Stack>
    );
};
